/**
 * GuardLocationPanel
 *
 * Side panel showing guard list with filters and search.
 */

import React, { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Link,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import styles from './GuardLocationPanel.module.css';
import {
  GuardLocation,
  GuardLocationFilters,
  GuardSortOption,
  GuardStatus,
  STATUS_COLORS,
  STATUS_LABELS,
} from './types';
import { filterGuards, sortGuards } from './utils';
import classNames from 'classnames';

interface StatusFilterChipProps {
  status: GuardStatus;
  count: number;
  isActive: boolean;
  onChange: (status: GuardStatus, isActive: boolean) => void;
}

const StatusFilterChip: React.FC<StatusFilterChipProps> = ({
  status,
  count,
  isActive,
  onChange,
}) => (
  <Chip
    label={
      <Box className={styles.filterChipContent}>
        <Box
          className={styles.filterChipDot}
          style={{ backgroundColor: STATUS_COLORS[status] }}
        />
        <span>{STATUS_LABELS[status]}</span>
        <span className={styles.filterChipCount}>{count}</span>
      </Box>
    }
    onClick={() => onChange(status, !isActive)}
    variant={isActive ? 'filled' : 'outlined'}
    className={classNames(styles.filterChip, { [styles.filterChipActive]: isActive })}
    size="small"
  />
);

interface GuardListItemProps {
  guard: GuardLocation;
  isSelected: boolean;
  onClick: () => void;
  onViewDetails?: (guard: GuardLocation) => void;
  onSendMessage?: (guard: GuardLocation) => void;
  onSiteClick?: (addressId: number, siteName: string) => void;
}

const GuardListItem: React.FC<GuardListItemProps> = ({
  guard,
  isSelected,
  onClick,
  onViewDetails,
  onSendMessage,
  onSiteClick,
}) => {
  const isLowBattery = guard.batteryPercent !== null && guard.batteryPercent < 20;

  const handleDetailsClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the map-centering onClick
    onViewDetails?.(guard);
  };

  return (
    <Box
      className={classNames(styles.guardListItem, { [styles.selected]: isSelected })}
      onClick={onClick}
    >
      <Box
        className={styles.guardStatusIndicator}
        style={{ backgroundColor: STATUS_COLORS[guard.status] }}
      />
      <Box className={styles.guardListInfo}>
        <Typography variant="body2" className={styles.guardListName}>
          {guard.name}
        </Typography>
        <Box className={styles.guardListMeta}>
          {guard.currentPatrol ? (
            guard.currentPatrol.addressId && onSiteClick ? (
              <Link
                component="button"
                variant="caption"
                className={styles.guardListPatrol}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onSiteClick(guard.currentPatrol!.addressId!, guard.currentPatrol!.siteName);
                }}
                sx={{ textAlign: 'left' }}
              >
                {guard.currentPatrol.siteName}
              </Link>
            ) : (
              <Typography variant="caption" className={styles.guardListPatrol}>
                {guard.currentPatrol.siteName}
              </Typography>
            )
          ) : (
            <Typography variant="caption" className={styles.guardListNoPatrol}>
              Not on patrol
            </Typography>
          )}
          <Typography variant="caption" className={styles.guardListTime}>
            {guard.lastSeenFormatted}
          </Typography>
        </Box>
      </Box>
      {guard.batteryPercent !== null && (
        <Box className={classNames(styles.guardListBattery, { [styles.low]: isLowBattery })}>
          {isLowBattery && <BatteryAlertIcon className={styles.batteryIcon} />}
          <span>{guard.batteryPercent}%</span>
        </Box>
      )}
      {onSendMessage && (
        <Tooltip title="Send Message">
          <IconButton
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onSendMessage(guard);
            }}
            className={styles.detailsButton}
            aria-label="Send message to guard"
          >
            <MessageOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onViewDetails && (
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={handleDetailsClick}
            className={styles.detailsButton}
            aria-label="View guard details"
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export interface GuardLocationPanelProps {
  /** All guards data */
  guards: GuardLocation[];
  /** Current filter state */
  filters: GuardLocationFilters;
  /** Callback to update filters */
  onFiltersChange: (filters: Partial<GuardLocationFilters>) => void;
  /** Current sort option */
  sortBy: GuardSortOption;
  /** Callback to change sort */
  onSortChange: (sortBy: GuardSortOption) => void;
  /** Currently selected guard ID */
  selectedGuardId: number | null;
  /** Callback when a guard is selected (centers map) */
  onGuardSelect: (guardId: number) => void;
  /** Callback when "Details" button is clicked (opens modal) */
  onGuardViewDetails?: (guard: GuardLocation) => void;
  /** Callback when "Send Message" button is clicked */
  onGuardSendMessage?: (guard: GuardLocation) => void;
  /** Last update timestamp */
  lastUpdate: Date | null;
  /** Callback to trigger refresh */
  onRefresh: () => void;
  /** Whether data is currently loading */
  isLoading?: boolean;
  /** Callback when a site name is clicked to open site info */
  onSiteClick?: (addressId: number, siteName: string) => void;
}

const GuardLocationPanel: React.FC<GuardLocationPanelProps> = ({
  guards,
  filters,
  onFiltersChange,
  sortBy,
  onSortChange,
  selectedGuardId,
  onGuardSelect,
  onGuardViewDetails,
  onGuardSendMessage,
  lastUpdate,
  onRefresh,
  isLoading = false,
  onSiteClick,
}) => {
  const selectedRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (selectedGuardId !== null && selectedRef.current) {
      selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedGuardId]);

  // Count guards by status
  const statusCounts = guards.reduce<Record<GuardStatus, number>>(
    (acc, guard) => {
      acc[guard.status] = (acc[guard.status] || 0) + 1;
      return acc;
    },
    { active: 0, stale: 0, offline: 0, not_on_patrol: 0 }
  );

  // Filter and sort guards
  const filteredGuards = filterGuards(guards, filters);
  const sortedGuards = sortGuards(filteredGuards, sortBy);

  // Handle filter toggle
  const handleFilterToggle = (status: GuardStatus, isActive: boolean) => {
    const filterKey = `show${status.charAt(0).toUpperCase()}${status
      .slice(1)
      .replace(/_([a-z])/g, (_, c) => c.toUpperCase())}` as keyof GuardLocationFilters;
    onFiltersChange({ [filterKey]: isActive });
  };

  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ searchQuery: e.target.value });
  };

  // Format last update time
  const lastUpdateFormatted = lastUpdate
    ? lastUpdate.toLocaleTimeString()
    : 'Never';

  return (
    <Box className={styles.panel}>
      {/* Header */}
      <Box className={styles.panelHeader}>
        <Typography variant="h6" className={styles.panelTitle}>
          Guard Locations
        </Typography>
        <Tooltip title={isLoading ? 'Data is currently refreshing' : 'Refresh'}>
          <span>
            <IconButton
              onClick={onRefresh}
              disabled={isLoading}
              size="small"
              className={classNames(styles.refreshButton, { [styles.spinning]: isLoading })}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Last update */}
      <Box className={styles.lastUpdate}>
        Last updated: {lastUpdateFormatted}
      </Box>

      {/* Search */}
      <Box className={styles.searchContainer}>
        <TextField
          placeholder="Search guards, sites..."
          value={filters.searchQuery}
          onChange={handleSearch}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Filters */}
      <Box className={styles.filtersContainer}>
        <StatusFilterChip
          status="active"
          count={statusCounts.active}
          isActive={filters.showActive}
          onChange={handleFilterToggle}
        />
        <StatusFilterChip
          status="stale"
          count={statusCounts.stale}
          isActive={filters.showStale}
          onChange={handleFilterToggle}
        />
        <StatusFilterChip
          status="not_on_patrol"
          count={statusCounts.not_on_patrol}
          isActive={filters.showNotOnPatrol}
          onChange={handleFilterToggle}
        />
        <StatusFilterChip
          status="offline"
          count={statusCounts.offline}
          isActive={filters.showOffline}
          onChange={handleFilterToggle}
        />
      </Box>

      {/* Sort */}
      <Box className={styles.sortContainer}>
        <FormControl size="small" fullWidth>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={sortBy}
            label="Sort by"
            onChange={(e) => onSortChange(e.target.value as GuardSortOption)}
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="lastSeen">Last Seen</MenuItem>
            <MenuItem value="status">Status</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Guard list */}
      <Box className={styles.guardList}>
        {sortedGuards.length === 0 ? (
          <Box className={styles.emptyState}>
            <Typography variant="body2" color="textSecondary">
              No guards match your filters
            </Typography>
          </Box>
        ) : (
          sortedGuards.map((guard) => (
            <div
              key={guard.guardId}
              ref={selectedGuardId === guard.guardId ? selectedRef : null}
            >
              <GuardListItem
                guard={guard}
                isSelected={selectedGuardId === guard.guardId}
                onClick={() => onGuardSelect(guard.guardId)}
                onViewDetails={onGuardViewDetails}
                onSendMessage={onGuardSendMessage}
                onSiteClick={onSiteClick}
              />
            </div>
          ))
        )}
      </Box>

      {/* Summary */}
      <Box className={styles.summary}>
        Showing {sortedGuards.length} of {guards.length} guards
      </Box>
    </Box>
  );
};

export default GuardLocationPanel;
